import type { LocalizedTemplateWithValues } from '@libs/types';

/**
 * One Organization Profile field a domain consumes (resolved via the field's
 * standard-document consumers), with whether the tenant has filled it.
 */
export interface DomainProfileFieldNeed {
  fieldId: string;
  key: string;
  label: string;
  filled: boolean;
}

/** The Organization Profile fields a single domain depends on, with fill roll-up. */
export interface DomainProfileFieldNeeds {
  domainKey: string;
  totalFields: number;
  filledFields: number;
  missingFields: number;
  /** True when the domain consumes no fields, or every consumed field is filled. */
  complete: boolean;
  fields: DomainProfileFieldNeed[];
}

/**
 * Mirrors the backend `hasValue` rule: null/blank string/empty array are empty;
 * everything else (including `false`/`0`) counts as filled.
 */
function hasFieldValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Derives a domain's Organization Profile field needs purely client-side from
 * the active template-with-values and the domain's standard-document keys: a
 * field belongs to the domain when one of its consumers feeds one of those
 * documents. Fields are deduped by id and ordered for rendering.
 */
export function computeDomainProfileFieldNeeds(input: {
  domainKey: string;
  standardDocumentKeys: string[];
  template: LocalizedTemplateWithValues;
}): DomainProfileFieldNeeds {
  const { domainKey, standardDocumentKeys, template } = input;
  const docKeys = new Set(standardDocumentKeys);

  const byId = new Map<string, DomainProfileFieldNeed>();
  for (const category of template.categories) {
    for (const field of category.fields) {
      const feedsDomain = field.consumers.some((consumer) =>
        docKeys.has(consumer.standardDocumentKey),
      );
      if (!feedsDomain || byId.has(field.id)) continue;
      byId.set(field.id, {
        fieldId: field.id,
        key: field.key,
        label: field.label ?? field.key,
        filled: hasFieldValue(field.currentValue),
      });
    }
  }

  const fields = [...byId.values()];
  const filledFields = fields.filter((field) => field.filled).length;
  const totalFields = fields.length;

  return {
    domainKey,
    totalFields,
    filledFields,
    missingFields: totalFields - filledFields,
    complete: totalFields === 0 || filledFields === totalFields,
    fields,
  };
}
